import { useState } from 'react';
import { Field, FieldLabel, FieldGroup } from '@/components/ui/field';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectGroup, SelectItem } from '@/components/ui/select';
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from '@/components/ui/table';
import { formatInstant } from '@/lib/utils/date';
import { activeBirthEvents, instant, number } from './birthCalculations';
import { newbornGrowth, type GrowthRow } from './newbornGrowth';
import type { ClinicalEvent, ClinicalSubject } from './types';
import type { PracticeConfiguration } from './rules';
export interface NewbornGrowthPanelProps {
  subject: ClinicalSubject;
  episodeId: string;
  events: ClinicalEvent[];
  birthAt?: string;
  sex?: 'MALE' | 'FEMALE';
  gaAtBirthDays?: number;
  configuration?: PracticeConfiguration;
  timeZone: string;
}
export function newbornGrowthMeasurements(
  events: ClinicalEvent[],
  episodeId: string,
  subjectId: string,
  birthAt: string | undefined,
  now: string,
) {
  const birth = instant(birthAt);
  const eligible = activeBirthEvents(events, episodeId, now).filter(
    (e) =>
      e.subject_id === subjectId &&
      e.event_type === 'NB.MEASUREMENTS' &&
      birth !== null &&
      instant(e.occurred_at)! >= birth,
  );
  return (['weight', 'length', 'head_circumference'] as const).map((metric) => {
    const field = { weight: 'weight_g', length: 'length_cm', head_circumference: 'head_circ_cm' }[metric];
    const event = [...eligible].reverse().find((e) => number(e.payload[field]) && (e.payload[field] as number) > 0);
    return {
      metric,
      unit: metric === 'weight' ? ('g' as const) : ('cm' as const),
      event,
      value: event ? (event.payload[field] as number) : null,
      postnatal_days: event && birth !== null ? Math.floor((instant(event.occurred_at)! - birth) / 86400000) : null,
    };
  });
}
export function NewbornGrowthPanel(props: NewbornGrowthPanelProps) {
  return <GrowthPanelContent key={`${props.episodeId}:${props.subject.id}`} {...props} />;
}
function GrowthPanelContent({
  subject,
  episodeId,
  events,
  birthAt,
  sex,
  gaAtBirthDays,
  configuration,
  timeZone,
}: NewbornGrowthPanelProps) {
  const [referenceId, setReferenceId] = useState(''),
    [chosenSex, setChosenSex] = useState<'MALE' | 'FEMALE' | ''>(''),
    [basis, setBasis] = useState<GrowthRow['age_basis']>('POSTNATAL_DAYS');
  const references = configuration?.clinically_reviewed ? (configuration.newborn_growth?.references ?? []) : [];
  const reference = references.find((r) => r.id === referenceId && r.enabled && r.clinically_reviewed);
  const referenceSex = sex ?? chosenSex;
  const measurements = newbornGrowthMeasurements(events, episodeId, subject.id, birthAt, new Date().toISOString());
  return (
    <section className="flex flex-col gap-4" aria-label={`Growth comparison for ${subject.label}`}>
      <h4 className="font-medium">Newborn growth reference comparison</h4>
      <p>
        Use a reviewed source and its age basis. These comparisons use confirmed measurements only; they do not change
        demographics or pregnancy dating.
      </p>
      <FieldGroup>
        <Field>
          <FieldLabel>Reviewed reference</FieldLabel>
          <Select value={referenceId || 'none'} onValueChange={(v) => setReferenceId(v === 'none' ? '' : v)}>
            <SelectTrigger aria-label="Reviewed growth reference">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="none">Choose reference</SelectItem>
                {references
                  .filter((r) => r.enabled && r.clinically_reviewed)
                  .map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.provider} · {r.version}
                    </SelectItem>
                  ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
        {sex ? (
          <p>Recorded reference sex: {sex.toLowerCase()}</p>
        ) : (
          <Field>
            <FieldLabel>Reference sex (explicit selection)</FieldLabel>
            <Select
              value={chosenSex || 'none'}
              onValueChange={(v) => setChosenSex(v === 'none' ? '' : (v as 'MALE' | 'FEMALE'))}>
              <SelectTrigger aria-label="Growth reference sex">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="none">Not selected</SelectItem>
                  <SelectItem value="FEMALE">Female reference</SelectItem>
                  <SelectItem value="MALE">Male reference</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>
        )}
        <Field>
          <FieldLabel>Reference age basis</FieldLabel>
          <Select value={basis} onValueChange={(v) => setBasis(v as GrowthRow['age_basis'])}>
            <SelectTrigger aria-label="Growth reference age basis">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="POSTNATAL_DAYS">Completed postnatal days</SelectItem>
                <SelectItem value="GESTATIONAL_DAYS">Gestational days at measurement</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
      </FieldGroup>
      {!reference && (
        <p>No enabled reviewed reference selected. Import and review source data in practice configuration.</p>
      )}
      {reference && <p>Source: {reference.citation}</p>}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Measurement</TableHead>
            <TableHead>Documented value</TableHead>
            <TableHead>Reference age</TableHead>
            <TableHead>Comparison</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {measurements.map((m) => {
            const age =
              m.postnatal_days === null
                ? null
                : basis === 'POSTNATAL_DAYS'
                  ? m.postnatal_days
                  : number(gaAtBirthDays) && Number.isInteger(gaAtBirthDays)
                    ? gaAtBirthDays + m.postnatal_days
                    : null;
            const output =
              m.value !== null && age !== null && referenceSex
                ? newbornGrowth(
                    {
                      sex: referenceSex,
                      metric: m.metric,
                      age_basis: basis,
                      age_days: age,
                      unit: m.unit,
                      value: m.value,
                    },
                    reference,
                  )
                : null;
            return (
              <TableRow key={m.metric}>
                <TableCell>{m.metric.replaceAll('_', ' ')}</TableCell>
                <TableCell>
                  {m.value === null ? 'Not documented' : `${m.value} ${m.unit}`}
                  {m.value !== null && m.metric === 'weight' && (
                    <p className="text-xs text-muted-foreground">
                      {Math.floor(m.value / 453.59237)} lb{' '}
                      {((m.value / 453.59237 - Math.floor(m.value / 453.59237)) * 16).toFixed(1)} oz
                    </p>
                  )}
                  {m.event && (
                    <p className="text-muted-foreground text-xs">
                      {formatInstant(m.event.occurred_at, 'MMM d, yyyy HH:mm', timeZone)} · Source {m.event.id}
                    </p>
                  )}
                </TableCell>
                <TableCell>{age === null ? 'Unavailable' : `${age} days`}</TableCell>
                <TableCell>
                  {output?.value
                    ? `${output.value.percentile.toFixed(1)}th percentile · z ${output.value.z_score.toFixed(2)}`
                    : (output?.warnings.join(' ') ??
                      'Confirmed measurement, birth time, explicit sex and age basis required.')}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </section>
  );
}
